// Pure helper extracted from AuthTokenSetter so the facility-resolution
// logic can be unit-tested independently of react-oidc-context.

export type FacilityMapping = Record<string, unknown>;

export type FacilityResolution =
  | { kind: 'select'; facId: number; facName: string }
  | { kind: 'noAccess'; reason: 'no-mappings' | 'first-mapping-invalid' };

function getProp<T>(obj: FacilityMapping, ...keys: string[]): T | undefined {
  for (const k of keys) {
    if (k in obj) return obj[k] as T;
  }
  const lower = keys.map((k) => k.toLowerCase());
  for (const objKey of Object.keys(obj)) {
    if (lower.includes(objKey.toLowerCase())) return obj[objKey] as T;
  }
  return undefined;
}

const idOf = (f: FacilityMapping) => (getProp<number>(f, 'pKey_Facility', 'PKey_Facility') ?? 0) as number;

/**
 * Resolve which facility the user should land on, given their `tblPMSec_Fac_Dietech`
 * mappings (GET Facility/GetAssociated), their saved `DF_Dietech_Facility`, the facility this
 * tab already had open and the facilities they used most recently.
 *
 * Facility ACCESS is the mappings and nothing else. Every other input is only a preference among
 * them: an id that is not one of the mappings is ignored, whatever it came from.
 *
 * Rules, first match wins:
 *  - 0 mappings → no access
 *  - `tabFacility` (the facility this browser tab already had open, from
 *    sessionStorage) matches a mapping → keep it. A page refresh must not throw
 *    the user back to their default mid-session; WinForms keeps the facility
 *    picked until the program closes. A new tab or a fresh connection pick has
 *    none.
 *  - DF > 0 matches a mapping → use it. An explicit default always wins over
 *    "last used".
 *  - `recentFacilityIds` (most recent first, from utils/recentFacilities.ts) → the
 *    most recent one that matches a mapping. The caller passes only the ids
 *    recorded for THIS connection: the same number is a different facility in
 *    another database. A user with no default lands where they last worked,
 *    not on whichever facility happens to sort first.
 *  - otherwise the mapping with the lowest PKey_Facility.
 *    WinForms falls back to the first row of its facility list, which is
 *    ORDER BY PKey_Facility (frmSplash / Fill_dtDietech_Facility_ID_Name); the
 *    API's list is ordered by name, so "first in the list" would differ.
 *  - No mapping has a valid PKey_Facility → no access (defensive)
 */
export function resolveFacilityFromMappings(
  defaultFacility: number,
  facilities: FacilityMapping[],
  tabFacility = 0,
  recentFacilityIds: readonly number[] = [],
): FacilityResolution {
  if (facilities.length === 0) {
    return { kind: 'noAccess', reason: 'no-mappings' };
  }

  const valid = facilities.filter((f) => idOf(f) > 0);
  const find = (id: number) => (id > 0 ? valid.find((f) => idOf(f) === id) : undefined);
  const recent = () => {
    for (const id of recentFacilityIds) {
      const f = find(id);
      if (f) return f;
    }
    return undefined;
  };
  const chosen =
    find(tabFacility) ??
    find(defaultFacility) ??
    recent() ??
    valid.reduce<FacilityMapping | undefined>((low, f) => (!low || idOf(f) < idOf(low) ? f : low), undefined);

  if (!chosen) {
    return { kind: 'noAccess', reason: 'first-mapping-invalid' };
  }
  const facName = String(getProp<string>(chosen, 'facility_Name', 'Facility_Name') ?? '');
  return { kind: 'select', facId: idOf(chosen), facName };
}
